import React, { useState } from 'react';
import { Clock, Package, Eye } from 'lucide-react';
import type { HistorialRecepcion } from '../types/ordenes';
import ModalHistorialRecepciones from './ModalHistorialRecepciones';

interface HistorialRecepcionesProps {
  historial: HistorialRecepcion[];
  nombreIngrediente: string;
  unidadMedida: string;
  cantidadRecibida: number;
  cantidadSolicitada: number;
}

const HistorialRecepciones: React.FC<HistorialRecepcionesProps> = ({
  historial,
  nombreIngrediente,
  unidadMedida,
  cantidadRecibida,
  cantidadSolicitada,
}) => {
  const [modalAbierto, setModalAbierto] = useState(false);

  const formatearFecha = (fecha: Date | string) => {
    return new Date(fecha).toLocaleDateString('es-CL', {
      month: '2-digit',
      day: '2-digit',
    });
  };

  const hayHistorial = historial && historial.length > 0;

  if (!hayHistorial) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400 italic">
        Sin historial de recepciones
      </div>
    );
  }

  const cantidadTotalRecibida = historial.reduce(
    (sum, h) => sum + h.cantidadRecibida,
    0
  );

  return (
    <>
      <div className="text-xs space-y-2">
        {/* Resumen compacto */}
        <div className="flex items-center justify-between">
          <div className="flex items-center text-gray-600 dark:text-gray-300">
            <Package className="h-3 w-3 mr-1" />
            <span>
              {historial.length} recepción{historial.length !== 1 ? 'es' : ''}
            </span>
          </div>
          <button
            onClick={() => setModalAbierto(true)}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
          >
            <Eye className="h-3 w-3 mr-1" />
            Ver historial
          </button>
        </div>

        {/* Resumen de recepciones más recientes */}
        <div className="space-y-1">
          {historial
            .sort(
              (a, b) =>
                new Date(b.fechaCreacion).getTime() -
                new Date(a.fechaCreacion).getTime()
            )
            .slice(0, 2)
            .map((recepcion, index) => (
              <div
                key={recepcion.id || index}
                className="flex items-center justify-between text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span>
                    {recepcion.cantidadRecibida} {unidadMedida}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  <span>{formatearFecha(recepcion.fechaCreacion)}</span>
                </div>
              </div>
            ))}

          {historial.length > 2 && (
            <div className="text-center text-gray-400 dark:text-gray-500 text-xs">
              ... y {historial.length - 2} más
            </div>
          )}
        </div>

        {/* Progreso */}
        <div className="mt-2">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500 dark:text-gray-400">
              Recibido: {cantidadTotalRecibida} / {cantidadSolicitada}{' '}
              {unidadMedida}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {Math.round((cantidadTotalRecibida / cantidadSolicitada) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
              style={{
                width: `${Math.min((cantidadTotalRecibida / cantidadSolicitada) * 100, 100)}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Modal para historial detallado */}
      <ModalHistorialRecepciones
        isOpen={modalAbierto}
        onClose={() => setModalAbierto(false)}
        historial={historial}
        nombreIngrediente={nombreIngrediente}
        unidadMedida={unidadMedida}
        cantidadSolicitada={cantidadSolicitada}
        cantidadRecibida={cantidadRecibida}
      />
    </>
  );
};

export default HistorialRecepciones;
